import { defineComponent, h } from 'vue';
import { QInput, QSelect, QIcon, QBtn, QTooltip } from 'quasar';
import utils from '../../../Shared/utils.jsx';
import methods from '../../../Shared/methods';
import { WithProxy } from '@/Shared/helperClasses';
import { wrapPropertyGridRow } from './pg2Wrap.jsx';
import { openSchemaDocumentInTab, resolveSchemaDocumentUrl } from './schemaDocumentLink.js';

export default defineComponent({
    name: 'form2-lookup-list',
    components: { QInput, QSelect, QIcon, QBtn, QTooltip },
    data: function () {
        return {
            condition_expn: null,
            list: null,
            lookupurl: null,
            lookupexpn: null,
            displayexpn: null,
            displayfield: null,
            valueexpn: null,
            valuefield: null,
            valuetemp: null,
            filter: '',
        };
    },
    created() {
        if (this.schema.formatData.LookupType === 'URL' || (!this.schema.formatData.LookupType && this.schema.formatData.LookupURL))
            this.lookupurl = utils.compile(this, this.schema.formatData.LookupURL);
        else if (this.schema.formatData.LookupType === 'Interpolated')
            this.lookupexpn = utils.compileExpression(this, this.schema.formatData.LookupInterpolatedExpression);

        const modelAs = this.schema.formatData.LookupModelAs || 'cmodel';
        if (this.schema.formatData.DisplayType === 'Expression')
            this.displayexpn = new Function('context', modelAs, 'with (context) return ' + this.schema.formatData.DisplayExpression + ';');
        else
            this.displayfield = new Function(modelAs, 'return ' + modelAs + '.' + (this.schema.formatData.DisplayField || 'Name'));

        if (!this.schema.formatData.ValueType || this.schema.formatData.ValueType === 'Expression')
            this.valueexpn = new Function('context', modelAs, 'with (context) return ' + this.schema.formatData.ValueExpression + ';');
        else this.valuefield = new Function(modelAs, 'return ' + modelAs + '.' + (this.schema.formatData.ValueField || 'Value'));
    },
    async mounted() {
        this.Refresh();
    },
    computed: {
        ...utils.forms.computed,
        filteredSelectOptions() {
            const items = this.buildCustomInputItems();
            if (!this.filter) return items;
            const q = this.filter.toLowerCase();
            return items.filter((i) => String(i.text ?? '').toLowerCase().includes(q));
        },
    },
    methods: {
        ...methods,
        renderOpenSchemaSlot(schemaUrl, value) {
            if (!schemaUrl)
                return undefined;

            return {
                append: () => (
                    <QBtn
                        flat
                        dense
                        round
                        size="sm"
                        icon="mdi-open-in-new"
                        class="q-ml-xs"
                        onClick={(e) => openSchemaDocumentInTab(e, schemaUrl, value)}
                    >
                        <QTooltip>Open schema document</QTooltip>
                    </QBtn>
                ),
            };
        },
        sync(prop, value) {
            console.log(`lookupList.sync('${prop}', '${value}')`);

            this.valuetemp = value;

            let v = value;
            if (v == '!!null!!') v = null;

            if (Array.isArray(this.cmodel)) {
                if (prop < this.cmodel.length) this.cmodel[prop] = v;
                else {
                    while (this.cmodel.length < prop) this.cmodel.push(null);

                    this.cmodel.push(v);
                }
            } else {
                this.cmodel[prop] = v;
            }
        },
        buildCustomInputItems() {
            const items = [];
            if (!this.list) return items;

            const proxy = new WithProxy(this).proxy;
            for (let i = 0; i < this.list.length; i++) {
                const item = this.list[i];
                const value = this.valueexpn ? this.valueexpn(proxy, item) : this.valuefield(item);
                const txt = this.displayexpn ? this.displayexpn(proxy, item) : this.displayfield(item);
                items.push({ text: txt, value: value });
            }
            return items;
        },
        filterFn(val, update) {
            update(() => {
                this.filter = val;
            });
        },
        getItemProps(item) {
            return {
                title: item.text,
                value: item.value,
                disabled: item.disabled,
            };
        },
        async Refresh() {
            if (this.lookupurl) {
                this.list = await utils.api.get(utils.evaluate(this.lookupurl, this), false, this.schema.formatData.CacheLookupResult);
            } else if (this.lookupexpn) {
                this.list = utils.evaluate(this.lookupexpn, this, false);
            }

            if (this.list && typeof this.list === 'object' && !Array.isArray(this.list)) {
                this.list = Object.keys(this.list).reduce((out, key) => {
                    out.push({
                        Key: key,
                        Value: this.list[key],
                    });
                    return out;
                }, []);
            }

            this.valuetemp = this.cmodel[this.schemakey];
        },
    },
    props: {
        name: '',
        schemakey: '',
        root: null,
        schema: null,
        cmodel: null,
        extra: null,
        readonly: false,
        child: 0,
        propertygrid: false,
        arrayItemInline: { type: Boolean, default: false },
    },
    render() {
        if (!this.Condition) return null;

        let desc;
        if (this.schema.description)
            desc = (
                <i
                    class="mdi mdi-information"
                    title={this.schema.description}
                    style={{ color: 'var(--v-checkboxGrey-base)', fontSize: 'small', marginLeft: '3px' }}
                ></i>
            );

        let err;
        if (this.schema.formatData?.error)
            err = utils.tt(this.schema.formatData.error, <QIcon name="mdi-alert" size="xs" color="negative" />);

        const name = <span style={{ marginLeft: this.child * 15 + 'px' }}>{this.name}</span>;
        const modelValue = this.cmodel?.[this.schemakey];
        const schemaDocumentUrl = resolveSchemaDocumentUrl(this.schemakey, this.cmodel, modelValue);
        const openSchemaSlot = this.renderOpenSchemaSlot(schemaDocumentUrl, modelValue);

        const labelSlot = () => (
            <span class="c-PropertyGrid2__labelInner">
                {name} {desc}
                {err}
            </span>
        );

        if (!this.list)
            return wrapPropertyGridRow(this, labelSlot, () => (
                <QInput
                    borderless
                    dense
                    hide-bottom-space
                    loading
                    class="caption pa-0 ma-0"
                    style={{ width: '100%' }}
                    modelValue={modelValue}
                    onUpdate:modelValue={(value) => this.sync(this.schemakey, value)}
                    v-slots={openSchemaSlot}
                />
            ));

        const items = [];
        const schema = this.schema;

        const proxy = new WithProxy(this).proxy;

        if (schema.formatData && schema.formatData.AllowCustomInput) {
            return wrapPropertyGridRow(this, labelSlot, () => (
                <QSelect
                    borderless
                    dense
                    hide-bottom-space
                    style={this.propertygrid ? { width: '100%', minWidth: 0 } : { minWidth: '150px' }}
                    popup-content-class="q-select-max-popup-height"
                    use-input
                    hide-selected
                    fill-input
                    autocomplete="off"
                    emit-value
                    map-options
                    input-debounce={0}
                    new-value-mode="add-unique"
                    modelValue={this.valuetemp}
                    options={this.filteredSelectOptions}
                    option-label="text"
                    option-value="value"
                    options-dense
                    v-slots={openSchemaSlot}
                    onFilter={this.filterFn}
                    onInputValue={(value) => {
                        this.valuetemp = value;
                    }}
                    onUpdate:modelValue={(value) => {
                        this.filter = '';
                        this.sync(this.schemakey, value);
                    }}
                    onPopupHide={() => {
                        this.filter = '';
                    }}
                    onBlur={() => this.sync(this.schemakey, this.valuetemp)}
                />
            ));
        }

        let found = false;
        for (var i = 0; i < this.list.length; i++) {
            const item = this.list[i];
            const value = this.valueexpn ? this.valueexpn(proxy, item) : this.valuefield(item);
            const txt = this.displayexpn ? this.displayexpn(proxy, item) : this.displayfield(item);

            if (this.valuetemp == value) found = true;

            items.push({
                text: txt,
                value: value,
                disabled: false,
            });
        }

        if (schema.$allows_null) items.splice(0, 0, { text: '', value: '!!null!!', disabled: false });
        else if (!found) items.splice(0, 0, { text: 'Please choose one', value: this.valuetemp, disabled: true });

        return wrapPropertyGridRow(this, labelSlot, () => (
            <QSelect
                borderless
                dense
                hide-bottom-space
                style={this.propertygrid ? { width: '100%', minWidth: 0 } : undefined}
                modelValue={this.valuetemp}
                popup-content-class="q-select-max-popup-height"
                options={items}
                option-label="text"
                option-value="value"
                options-dense
                emit-value
                map-options
                v-slots={openSchemaSlot}
                onUpdate:modelValue={(value) => this.sync(this.schemakey, value)}
            />
        ));
    },
});
